import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ChartApiService,
  ChartPayload,
  ChartTurnoverResponse,
  GraphMode,
  GraphSize,
  TurnoverGraphDataType,
} from '@app/+authenticated/chart';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { DashboardFilterType, withoutHiddenWidgets } from '@app/reducers/page-filters/page-filters-dashboard.config';
import { selectAllowedDashboardFilters } from '@app/reducers/page-filters/page-filters-dashboard.selectors';
import { useHrProOrUpsellVisible } from '@app/services/hr-pro-upsell/use-hr-pro-or-upsell-visible';
import { RefinerService } from '@app/services/refiner.services';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getPermissionState } from '@reducers/auth/permission.helper';
import { AppState } from '@reducers/interfaces';
import { DashboardFilterState } from '@reducers/page-filters/page-filters.model';
import { mgcIconClassifyAdd2Regular } from '@sb/mgc-icons/mgcIcon-classify_add_2_regular.icon';
import { mgcIconLookUpRegular } from '@sb/mgc-icons/mgcIcon-look_up_regular.icon';
import { mgcIconSettings6Regular } from '@sb/mgc-icons/mgcIcon-settings_6_regular.icon';
import { FeatureService } from '@sb/shared/feature';
import { hasPermission } from '@sb/shared/permission/util';
import { IconRegistry } from '@sb/ui/icon';
import { ProgressBarModel } from '@sb/ui/progress-bar';
import { OnboardingChecklistService } from '@shared/onboarding/onboarding-checklist.service';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { clsx } from 'clsx';
import { subWeeks } from 'date-fns';
import { combineLatest, of as observableOf, Subscription } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { SidebarComponent } from '../../shared/sidebar/sidebar.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'my-dashboard',
  templateUrl: './my-dashboard.component.html',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  host: { '[class]': 'hostClasses' },
})
export class MyDashboardComponent implements OnInit, OnDestroy {
  private chartApi = inject(ChartApiService);
  private store = inject<Store<AppState>>(Store);
  private translate = inject(TranslateService);
  private refinerService = inject(RefinerService);
  private iconRegistry = inject(IconRegistry);
  #featureService = inject(FeatureService);
  #hrProVisible$ = toObservable(useHrProOrUpsellVisible());

  onboardingChecklistService = inject(OnboardingChecklistService);

  hostClasses = clsx('block size-full overflow-hidden');

  // Under the side-nav labs layout the widgets sidebar becomes a left panel
  // that pushes the dashboard content right instead of overlaying it.

  // The sidebar lives inside a labs/legacy @if branch, so it isn't available
  // until after the view is resolved.
  @ViewChild(SidebarComponent)
  sidebar: SidebarComponent;

  // Tracks the panel's open state via two-way binding on the sidebar so it
  // reflects every transition (toggle, close button, outside click). Drives the
  // active state of the "Customise dashboard" toggle, mirroring filters-toggle
  // under the side-nav labs layout.
  panelOpen = signal(false);

  chartPayload: ChartPayload = {
    from: toISODateString(subWeeks(new Date(), 2)),
    to: toISODateString(new Date()),
    group_by_period: 'day',
    group_by_location: 'department',
  };

  isLoading = false;
  graphData: ChartTurnoverResponse;

  planType = PlanType;

  private dataSubs = new Subscription();
  dashboardFilters: DashboardFilterState;
  dashboardFilterType = DashboardFilterType;
  visibleWidgetCount = signal(0);

  turnOverGraphDataTypes = TurnoverGraphDataType;
  turnOverGraphSize: GraphSize = 'small';
  turnOverGraphMode: GraphMode = 'widget';
  turnOverGraphConfigs = [
    {
      filterType: DashboardFilterType.ABSENCE_PERCENTAGE,
      graphType: TurnoverGraphDataType.SICK_LEAVE_PERCENTAGE,
    },
    {
      filterType: DashboardFilterType.SALARY_COST_PERCENTAGE,
      graphType: TurnoverGraphDataType.SALARY_COST_PERCENTAGE,
    },
    {
      filterType: DashboardFilterType.SALARY_COST_PERCENTAGE_COC,
      graphType: TurnoverGraphDataType.COC_PERCENTAGE,
    },
    {
      filterType: DashboardFilterType.SALARY_PER_HOUR,
      graphType: TurnoverGraphDataType.SALARY_PER_HOUR,
    },
    {
      filterType: DashboardFilterType.TURNOVER_PER_HOUR,
      graphType: TurnoverGraphDataType.TURNOVER_PER_HOUR,
    },
  ];

  employeeGreetMessage: string;

  checklist = this.onboardingChecklistService.checklist;
  authUser = this.store.selectSignal(getAuthenticatedUser);
  hasChecklistWidget = computed(() => this.checklistItems()?.length > 0 && this.authUser());
  checklistItems = computed(() => this.checklist()?.items);
  checklistProgress = computed<ProgressBarModel>(() => ({
    level: this.checklist()?.progress ?? 0,
    theme: 'green',
  }));
  checklistTitle = computed(() =>
    this.onboardingChecklistService.getChecklistTitle(this.checklist()?.progress, {
      name: this.authUser()?.first_name,
    }),
  );

  // The layout reserves horizontal space for the side panel, so the grid caps at
  // two columns until 2xl instead of going to three at xl.
  widgetGridClasses = computed(() =>
    clsx(
      'grid gap-8 empty:hidden md:grid-cols-1',
      this.hasChecklistWidget() && this.visibleWidgetCount() < 3
        ? 'xl:flex xl:justify-center [&>*]:flex-1 [&>*]:xl:max-w-screen-lg'
        : 'lg:grid-cols-2 2xl:grid-cols-3',
    ),
  );

  constructor() {
    this.iconRegistry.registerIcons([mgcIconLookUpRegular, mgcIconClassifyAdd2Regular, mgcIconSettings6Regular]);
  }

  ngOnInit() {
    this.isLoading = true;

    this.dataSubs.add(
      this.store
        .select(getPermissionState)
        .pipe(
          switchMap((permissionState) => {
            const permission = hasPermission(
              {
                permissions: ['View reports'],
                userId: 'me',
                departments: 'any',
              },
              permissionState,
            );

            return permission ? this.chartApi.turnover(this.chartPayload) : observableOf(undefined);
          }),
          take(1),
          filter((graphData) => !!graphData),
        )
        .subscribe((graphData) => {
          this.graphData = graphData;
          this.isLoading = false;
        }),
    );

    this.dataSubs.add(
      combineLatest([this.store.pipe(select(selectAllowedDashboardFilters)), this.#hrProVisible$]).subscribe(
        ([allowedFilters, hrProVisible]) => {
          const dashboardFilters = withoutHiddenWidgets(allowedFilters, {
            isEnabled: (flag) => this.#featureService.isEnabled(flag),
            hrProVisible,
          });

          this.visibleWidgetCount.set(Object.values(dashboardFilters).filter((value) => !!value.visibility).length);
          this.dashboardFilters = dashboardFilters;
        },
      ),
    );

    this.employeeGreetMessage = this.generateGreetMessage();
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  private generateGreetMessage(): string {
    const today = new Date();
    const hours = today.getHours();

    if (hours >= 0 && hours < 7) {
      return this.translate.instant('Good night');
    }
    if (hours >= 7 && hours < 12) {
      return this.translate.instant('Good morning');
    }
    if (hours >= 12 && hours < 18) {
      return this.translate.instant('Good afternoon');
    }
    if (hours >= 18) {
      return this.translate.instant('Good evening');
    }
    return '';
  }

  toggleSideBar() {
    this.sidebar?.toggle();
  }

  openDashboardFeedbackSurvey() {
    const surveyId = 'dashboard-survey';
    this.refinerService.showSurvey(surveyId);
  }

  dismissChecklist() {
    void this.onboardingChecklistService.dismiss().subscribe();
  }
}
